<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';
import { DropdownChangeEvent } from 'primevue/dropdown';
import MultiSelect, { MultiSelectChangeEvent } from 'primevue/multiselect';
import OverlayPanel from 'primevue/overlaypanel';
import SelectButton from 'primevue/selectbutton';
import { can, useRouting, useDateTime, useRequestCancel, useIsEqual } from '@/composables';
import { useEventReportStore, useReportStore } from '@/store';
import { EventReportDownloadFileType } from '@/types';
import { PERMISSIONS, EMIT } from '@/enum';
import ReportSubjectTitle from '@/views/Reports/components/ReportSubjectTitle.vue';

/**
 * Define a routing instance for the utility.
 */
const $routing = useRouting();

/**
 * Define the store instances for event report data.
 */
const pageLoading = ref(true);

/**
 * Define all available events that can be emitted back to the parent.
 */
const emit = defineEmits([EMIT.QUERY_PARAMS]);

/**
 * Define the store instances for event report data.
 */
const reportStore = useReportStore();
const eventReportStore = useEventReportStore();
const {
  filters: {
    value: { eventTypes, therapyTypes, disciplines, attendees },
  },
} = storeToRefs(eventReportStore);

/**
 * The filter overlay reference object used to toggle the overlay.
 */
const filtersOverlay = ref();
const rmsReportToggleValue = ref('Events');
const eventReportTypes = computed(() => {
  const isRsm = reportStore.filters.organizations.isRsm;
  const availableReports = ['Events'];

  if (isRsm && can([PERMISSIONS.REPORTS_RSM_INDEX])) {
    availableReports.push('RSM Events');
  }

  return availableReports;
});

/**
 * Initialize everything required to build out the component.
 */
onMounted(() => init());

/**
 * cancel any and all reuests before unmounting
 */
onBeforeUnmount(() => {
  useRequestCancel().requestSaverAbortAll();
  $routing.router.push({ query: {} });
  eventReportStore.clearStoreFilters();
  pageLoading.value = true;
});

watch(
  () => $routing.route.query,
  async (newValue, oldValue) => {
    if (pageLoading.value) return;

    /* eslint-disable */
    const ignoredKeys = ['page', 'per_page', 'order', 'order_by'];
    const newFilteredValue = Object.fromEntries(
      Object.entries(newValue).filter(([key, _]) => !ignoredKeys.includes(key)),
    );
    const oldFilteredValue = Object.fromEntries(
      Object.entries(oldValue).filter(([key, _]) => !ignoredKeys.includes(key)),
    );

    const doNewRequest = !useIsEqual(newFilteredValue, oldFilteredValue);
    eventReportStore.queryParams = $routing.route.query;

    await defineFilters();

    if (doNewRequest) {
      await getEventsCount();
    }

    await doEventsReport();

    if (doNewRequest) {
      await getAttendees();
    }
  },
);

/**
 * Initialize the event report default values and data.
 */
const init = async () => {
  await defineFilters();
  await eventReportStore.getEventTypes();
  await eventReportStore.getDisciplines();
  await eventReportStore.getTherapyResultTypes();

  if (!reportStore.referrer) {
    reportStore.referrer = $routing.route.name;
  }

  if (pageLoading.value) {
    await getEventsCount();
    await doEventsReport();
    await getAttendees();
  }

  pageLoading.value = false;
  eventReportStore.clearStoreFilters();
};

/**
 * Execute the events report request based on the provided query param filters.
 */
const doEventsReport = async () => {
  const query = $routing.route.query;
  const filters = eventReportStore.defineFilters(query);
  await eventReportStore.getEventsReport(filters);
};

const getAttendees = async () => {
  await eventReportStore.getAttendees();
};

const getEventsCount = async () => {
  const query = $routing.route.query;
  const filters = eventReportStore.defineFilters(query);
  await eventReportStore.getEventsCount(filters);
};

/**
 * Define the filters based on the query params available on page load.
 */
const defineFilters = async () => {
  const query = $routing.route.query;
  eventReportStore.filters.order = query.order || eventReportStore.filters.order;
  eventReportStore.filters.order_by = query.order_by || eventReportStore.filters.order_by;
  eventReportStore.filters.page = parseInt(query.page) || eventReportStore.filters.page;
  eventReportStore.filters.per_page = parseInt(query.per_page) || eventReportStore.filters.per_page;

  const attendees: number[] = [];
  if (query.attendees) {
    query.attendees
      .toString()
      .split(',')
      .forEach((item) => attendees.push(+item));
  }
  eventReportStore.filters.attendees.selected = query.attendees ? attendees : null;

  const eventTypes: number[] = [];
  if (query.event_types) {
    query.event_types
      .toString()
      .split(',')
      .forEach((item) => eventTypes.push(item));
  }
  eventReportStore.filters.eventTypes.selected = query.event_types ? eventTypes : null;

  const disciplines: number[] = [];
  if (query.disciplines) {
    query.disciplines
      .toString()
      .split(',')
      .forEach((item) => disciplines.push(+item));
  }
  eventReportStore.filters.disciplines.selected = query.disciplines ? disciplines : null;

  const therapyTypes: number[] = [];
  if (query.therapy_result_types) {
    query.therapy_result_types
      .toString()
      .split(',')
      .forEach((item) => therapyTypes.push(+item));
  }
  eventReportStore.filters.therapyTypes.selected = query.therapy_result_types ? therapyTypes : null;

  const organizations: number[] = [];
  if (query.organizations) {
    query.organizations
      .toString()
      .split(',')
      .forEach((item) => organizations.push(+item));
  }

  eventReportStore.filters.organizations.selected = query.organizations ? organizations : null;
  if (query.is_rsm) {
    rmsReportToggleValue.value = query.is_rsm == '1' ? 'RSM Events' : 'Events';
    eventReportStore.events.rsmToggle = rmsReportToggleValue.value;
    eventReportStore.filters.is_rsm = Number(query.is_rsm);
  }

  // Add back the query params for the report once it regains focus again.
  if (eventReportStore.queryParams) {
    $routing.addQueryParams(eventReportStore.queryParams);
  } else {
    emit(EMIT.QUERY_PARAMS);
  }
};

/**
 * Handle the filtering of results based on the selected attendees.
 */
const onAttendeeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('attendees', event.value.join());
  } else {
    $routing.removeQueryParam('attendees');
  }
};

/**
 * Handle the filtering of results based on the selected event_types.
 */
const onEventTypeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('event_types', event.value.join());
  } else {
    $routing.removeQueryParam('event_types');
  }
};

/**
 * Handle the filtering of results based on the selected therapy_result_types.
 */
const onTherapyTypeChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('therapy_result_types', event.value.join());
  } else {
    $routing.removeQueryParam('therapy_result_types');
  }
};

/**
 * Handle the filtering of results based on the selected disciplines.
 */
const onDisciplineChange = (event: MultiSelectChangeEvent) => {
  if (event.value.length) {
    $routing.addQueryParam('disciplines', event.value.join());
  } else {
    $routing.removeQueryParam('disciplines');
  }
};

/**
 * Handle the RSM event filtering
 */
const onRsmToggleEvent = () => {
  eventReportStore.$patch({
    events: {
      rsmToggle: rmsReportToggleValue.value,
    },
  });

  if (can([PERMISSIONS.REPORTS_RSM_INDEX])) {
    const isRsm = eventReportStore.events.rsmToggle === 'RSM Events' ? '1' : '0';
    $routing.addQueryParam('is_rsm', isRsm);
    reportStore.is_rsm = Number(isRsm);
  }
};

/**
 * Handle the pagination of the request data.
 */
const onPage = (event: DataTablePageEvent) => {
  $routing.addQueryParams({ per_page: event.rows, page: event.page + 1 });
};

/**
 * Handle the ordering of the request data.
 */
const onSort = (event: DataTableSortEvent) => {
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  $routing.addQueryParams({ order, order_by: event.sortField });
};

/**
 * Handle the toggling of the available filter overlay.
 */
const onDisplayFilterOverlay = (event: DropdownChangeEvent) => filtersOverlay.value.toggle(event);

/**
 * Handle the export action for the specific report type.
 */
const onExportReport = async (fileType: EventReportDownloadFileType) => {
  await eventReportStore
    .downloadEventsReport($routing.route.query, fileType)
    .then((response) => {
      if (response.status === 202) {
        eventReportStore.doToast(
          'You will receive an email with a link to download the exported data.',
          'Your export has started',
        );
      }
      eventReportStore.events.downloading = false;
    })
    .catch((response) => {
      if (response.status === 422) {
        eventReportStore.doToast('Select only one organization to generate a report.', 'Error exporting report', true);
      }
      eventReportStore.events.downloading = false;
    });
};

/**
 * Define the tooltip text for the export buttons.
 */
const tooltipExportText = (fileType: EventReportDownloadFileType) =>
  eventReportStore.canDownload ? `Download as ${fileType}` : `Email as ${fileType}`;

/**
 * Define the goal type and stats.
 */
const defineGoal = (goal) =>
  goal.total_trials > 0 ? `Trials (${goal.trials}/${goal.total_trials}) ` : `Percentage (${goal.percentage}%) `;

/**
 * Define the goal description and determine whether or not to display a shortened version.
 */
const defineGoalDescription = (goal, tooltip: boolean) => {
  const description = goal.treatment_goal?.description;

  if (tooltip && description) return description;
  return description && description.length > 50 ? `${description.substring(0, 50)} (...)` : description;
};

/**
 * Define the full goal information to be displayed within the tooltip.
 */
const defineGoalTooltip = (goal) => `${defineGoal(goal)} ${defineGoalDescription(goal, true)}`;
</script>

<template>
  <div class="page-content">
    <Card>
      <template #content>
        <div class="py-3">
          <ReportSubjectTitle title="Events" :name="reportStore.organizations" :dates="reportStore.filters.dates" />
          <div class="flex flex-wrap gap-2 mt-3">
            <div v-if="can([PERMISSIONS.REPORTS_RSM_INDEX])">
              <SelectButton v-model="rmsReportToggleValue" :options="eventReportTypes" @change="onRsmToggleEvent" />
            </div>
            <div v-if="can([PERMISSIONS.REPORTS_FILTER_SERVICE_PROVIDERS])">
              <MultiSelect
                id="reports_events_attendee"
                v-model="attendees.selected"
                :options="attendees.options"
                :loading="attendees.loading"
                :filter="true"
                :reset-filter-on-hide="true"
                :show-toggle-all="false"
                class="w-full"
                option-label="name"
                option-value="value"
                placeholder="All Attendees"
                display="chip"
                @change="onAttendeeChange"
              />
            </div>
            <div v-if="can([PERMISSIONS.REPORTS_FILTER_VIEW])">
              <AppButton type="secondary" text="Filters" icon-left="filter_list" @click="onDisplayFilterOverlay" />
              <OverlayPanel ref="filtersOverlay">
                <div class="p-3">
                  <div class="field mb-2">
                    <label for="username">Event Type</label>
                    <MultiSelect
                      id="reports_events_event_type"
                      v-model="eventTypes.selected"
                      :options="eventTypes.options"
                      :loading="eventTypes.loading"
                      :filter="true"
                      :reset-filter-on-hide="true"
                      :show-toggle-all="false"
                      class="w-full"
                      option-label="name"
                      option-value="value"
                      placeholder="All Event Types"
                      display="chip"
                      @change="onEventTypeChange"
                    />
                  </div>
                  <div class="field mb-2">
                    <label for="username">Attendance Type</label>
                    <MultiSelect
                      id="reports_events_therapy_type"
                      v-model="therapyTypes.selected"
                      :options="therapyTypes.options"
                      :loading="therapyTypes.loading"
                      :filter="true"
                      :reset-filter-on-hide="true"
                      :show-toggle-all="false"
                      class="w-full"
                      option-label="name"
                      option-value="value"
                      placeholder="All Attendance Types"
                      display="chip"
                      @change="onTherapyTypeChange"
                    />
                  </div>
                  <div class="field">
                    <label for="username">Discipline</label>
                    <MultiSelect
                      id="reports_events_discipline"
                      v-model="disciplines.selected"
                      :options="disciplines.options"
                      :loading="disciplines.loading"
                      :filter="true"
                      :reset-filter-on-hide="true"
                      :show-toggle-all="false"
                      class="w-full"
                      option-label="name"
                      option-value="value"
                      placeholder="All Disciplines"
                      display="chip"
                      @change="onDisciplineChange"
                    />
                  </div>
                </div>
              </OverlayPanel>
            </div>
            <div v-if="can([PERMISSIONS.REPORTS_EVENT_SHOW_ALL_COLUMNS_VIEW])" class="flex-1">
              <div class="mr-2 pt-1" style="float: left">
                <InputSwitch v-model="eventReportStore.filters.allColumns" />
              </div>
              <div class="w-4rem text-gray-600 font-normal line-height-1" style="float: left">
                <label class="text-xs mt-1">Show All Columns</label>
              </div>
            </div>
            <div v-if="can([PERMISSIONS.REPORTS_EVENTS_DOWNLOAD_CSV])">
              <AppButton
                v-tooltip.bottom="tooltipExportText('CSV')"
                :disabled="!eventReportStore.events.total"
                :loading="eventReportStore.events.downloading"
                type="secondary"
                text="CSV"
                icon-left="system_update_alt"
                @click="onExportReport('csv')"
              />
            </div>
            <div v-if="can([PERMISSIONS.REPORTS_EVENTS_DOWNLOAD_XSLX])">
              <AppButton
                v-tooltip.bottom="tooltipExportText('XLSX')"
                :disabled="!eventReportStore.events.total"
                :loading="eventReportStore.events.downloading"
                type="secondary"
                text="XLSX"
                icon-left="system_update_alt"
                @click="onExportReport('xlsx')"
              />
            </div>
          </div>
        </div>
        <DataTable
          v-model:filters="eventReportStore.filters"
          filter-display="menu"
          :value="eventReportStore.events.data"
          :paginator="true"
          :rows="eventReportStore.filters.per_page"
          :total-records="eventReportStore.events.total"
          data-key="id"
          :row-hover="true"
          :lazy="true"
          loading-icon="logo-spinner"
          :loading="eventReportStore.events.loading"
          :rows-per-page-options="[15, 25, 30]"
          paginator-template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          current-page-report-template="Showing {first} to {last} of {totalRecords}"
          responsive-layout="scroll"
          :resizable-columns="true"
          column-resize-mode="fit"
          @page="onPage"
          @sort="onSort"
        >
          <template #empty>No events found</template>
          <template #loading>Loading event data. Please wait.</template>
          <template v-if="eventReportStore.showAllColumns">
            <Column field="date" header="Date" sortable>
              <template #body="{ data }">
                <span v-text="useDateTime().formatIntlDate(data.date)" />
              </template>
            </Column>
            <Column field="time" header="Time" />
            <Column field="event_type" header="Event Type" />
            <Column field="session_type" header="Session Type" />
            <Column field="organization" header="Organization">
              <template #body="{ data }">
                <span v-text="data.organization.length ? data.organization.join(',') : 'N/A'" />
              </template>
            </Column>
            <Column field="clinician.display_name" header="Service Provider" />
            <Column field="attendee.display_name" header="Attendee" />
            <Column field="attendee.externalAssociation.external_id" header="SSID" />
            <Column field="therapy_result_type" header="Attendance Type" />
            <Column field="group" header="Group" body-class="text-center" />
            <Column field="planned_minutes" header="Planned Minutes" body-class="text-center" />
            <Column field="actual_minutes" header="Actual Minutes" body-class="text-center" />
            <Column field="documented" header="Documented" />
            <Column field="notes" header="Notes">
              <template #body="{ data }">
                <div class="w-20rem white-space-normal">{{ data.notes }}</div>
              </template>
            </Column>
            <Column field="goals" header="Goals">
              <template #body="{ data }">
                <span v-if="data.goals === 'N/A' || !data.goals.length" v-text="'N/A'" />
                <template v-else>
                  <template v-for="(item, key) in data.goals" :key="key">
                    <div v-tooltip.left="defineGoalTooltip(item)" class="mb-3">
                      <span class="text-blue-600" v-text="defineGoal(item)" />
                      <span v-text="defineGoalDescription(item, false)" />
                    </div>
                  </template>
                </template>
              </template>
            </Column>
          </template>
          <template v-else>
            <Column field="date" header="Date" header-style="width: 200px" sortable>
              <template #body="{ data }">
                <span v-text="useDateTime().formatIntlDate(data.date)" />
              </template>
            </Column>
            <Column field="event_type" header="Event Type" header-style="width: 300px" />
            <Column field="attendee.display_name" header="Attendee" />
          </template>
        </DataTable>
      </template>
    </Card>
  </div>
</template>

<style lang="scss" scoped>
:deep(.p-card-body) {
  padding: 0 1rem 1rem 1rem;

  .p-datatable-header {
    background: $white;
    padding: 0.5rem;
  }

  .p-datatable-wrapper {
    margin: 0px !important;
  }

  .p-paginator-current {
    flex: 1;
    justify-content: flex-end;
  }
}
:deep(.p-selectbutton) {
  width: 100%;
  border-radius: 4px;
  .p-button {
    border-radius: 4px;
    &:last-of-type {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    &:first-of-type {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }
  .p-button {
    border-color: $grayLight;
    &.p-highlight {
      background: var(--primary-color-light);
      border-color: var(--primary-color-light);
      &:hover {
        background: var(--primary-color-light) !important;
        border-color: var(--primary-color-light) !important;
        color: var(--primary-color-dark) !important;
      }
      .p-button-label {
        color: var(--primary-color-dark);
      }
    }
  }
  .p-button-label {
    font-family: 'Rubik';
    font-size: 14px;
    font-weight: 600;
  }
}

.text-red-600 {
  color: $red;
}
</style>
